// Concise, learner-facing descriptions of WHO a commentator is / WHAT a work is
// about - the "source intelligence" that lets a learner choose what to open.
// Sefaria shows a one-liner per work; these are ORIGINAL rewordings in a tighter,
// warmer voice (never copied), kept short enough for a compact card.
//
// ACCURACY-LOCK: these are curated, hand-checked facts (era, place, character),
// NOT generated or guessed at runtime. A work we are not confident about is
// simply OMITTED (the card shows no description rather than a wrong one) - the
// same honest posture Sefaria takes when it greys out an undescribed work.
// Commentator accuracy is load-bearing here: Rambam != Ramban, Rashi != Rashbam.
//
// ASCII only (no em-dashes / curly quotes): cp1252 source constraint.

import { norm } from "./taxonomy.ts";

// Keyed by the normalized commentator / work title (norm() lowercases + strips
// punctuation), matching the same key convention era.ts / prominence.ts use.
const DESCRIPTIONS: Readonly<Record<string, string>> = {
  // --- Rishonim (classic commentators) ---
  rashi: "The first stop for every learner, clear and concise, drawing on Talmud and Midrash.",
  rashbam: "Rashi's grandson, chasing the plainest meaning even when it breaks with tradition.",
  "ibn ezra": "Sharp and witty, fascinated by grammar and the exact wording of the verse.",
  "second version of ibn ezra": "Ibn Ezra's expanded second pass on the Torah, with even more detail.",
  ramban: "Sweeping and profound, weaving together plain meaning, philosophy, and kabbalah.",
  sforno: "A Renaissance rabbi-physician's brief, rational, and elegant read of the text.",
  chizkuni: "A friendly distillation of Rashi and the French sages into clear plain meaning.",
  "bekhor shor": "A bold Tosafist who reads for the down-to-earth, straightforward sense.",
  radak: "A master grammarian who makes the language of the verse click into place.",
  ralbag: "A philosopher's reading that ends each passage with practical lessons for life.",
  abarbanel: "Opens every section with big questions, then answers them in sweeping detail.",
  "baal haturim": "Famous for dazzling gematria and the hidden patterns tucked inside the words.",
  "kitzur baal haturim": "The Baal HaTurim's plain-sense notes, without the number play.",
  "daat zekenim": "A collection of Torah gems from the great French and German Tosafists.",
  "hadar zekenim": "A Tosafist treasury of comments, sparkling with number play and hidden wit.",
  "rabbeinu bahya": "Unlocks the verse on four levels, from plain meaning to hidden mystical layers.",
  "paaneach raza": "A Tosafist's treasure hunt through number patterns and hidden meanings in the words.",
  "tur haaroch": "The fuller Torah commentary of the master who codified Jewish law.",
  "toledot yitzchak": "A concise, plain-sense commentary from the uncle of the Beit Yosef.",
  "saadia gaon": "The Gaon's groundbreaking translation that set the standard for plain meaning.",
  "rabbeinu chananel": "An early, concise voice carrying the wisdom of the Geonim.",
  meiri: "Calm, clear, and beautifully organized, a pleasure to learn with.",
  rosh: "Torah notes from one of the towering legal authorities of the Middle Ages.",
  alshich: "A master preacher who poses sharp questions and answers them in flowing sermons.",
  "minchat shai": "The definitive authority on the exact spelling and tradition of every word.",

  // --- Acharonim (later commentators) ---
  mizrachi: "The deep-dive supercommentary that defends and explains every word of Rashi.",
  "siftei chakhamim": "The friendly companion that unpacks Rashi, comment by comment.",
  "gur aryeh": "The Maharal's powerful, philosophical defense of Rashi.",
  "kli yakar": "Rich, moral, and homiletical, full of ideas for the Shabbos table.",
  "or hachaim": "Beloved for blending plain meaning with warm, soulful mysticism.",
  malbim: "Argues that every word is exact, and that midrash is the true plain sense.",
  "haamek davar": "The Netziv's flowing read, with a sharp ear for nuance and word choice.",
  "harchev davar": "The Netziv's deeper tangents and footnotes on his own Haamek Davar.",
  "rav hirsch": "Finds rich symbolism and ethics in every detail, written for the modern world.",
  "meshech hochma": "Brilliant, original flashes of insight from a towering Lithuanian mind.",
  "torah temimah": "Connects each verse to the Talmud and Midrash passages that quote it.",
  shadal: "Independent and bold, unafraid to wrestle with the plain text head-on.",
  "chatam sofer": "Torah insights from the fierce champion of tradition in 19th-century Europe.",
  chida: "A globe-trotting scholar's wide-ranging notes and rediscovered gems.",
  "tzeenah ureenah": "The beloved Yiddish classic that retells the parsha through its midrashim.",
  "avi ezer": "A close companion that clarifies Ibn Ezra's famously terse comments.",
  "haktav vehakabalah": "Reveals how the Oral Law is hiding inside the wording of the verse.",
  "beit halevi": "Sharp, conceptual derashot from the founder of the Brisker dynasty.",
  "pardes yosef": "A treasure chest of insights gathered from across the generations.",
  "tzafnat paneach": "The Rogatchover's dense, dazzling, almost mathematical genius.",
  reggio: "An Enlightenment scholar's clear translation and plain-sense read.",
  "metzudat david": "Plain and accessible, simply telling you what the verse means.",
  "metzudat zion": "Quick word definitions, the perfect partner to Metzudat David.",
  "aderet eliyahu": "The Vilna Gaon's piercing notes, where every word counts.",

  // --- Targum (translation) ---
  onkelos: "The ancient Aramaic translation, in many ways the very first commentary.",
  "targum jonathan": "An Aramaic translation that bursts with midrash and storytelling.",
  "targum yonatan": "An Aramaic translation that bursts with midrash and storytelling.",
  "targum yerushalmi": "Surviving fragments of an old Aramaic translation rich in early traditions.",
  "targum jerusalem": "Surviving fragments of an old Aramaic translation rich in early traditions.",

  // --- common Midrash / aggadah collections (prefix-matched, e.g. "Sifrei Bamidbar") ---
  "midrash rabbah": "The classic well of midrash, full of story, teaching, and deeper meaning.",
  "bereshit rabbah": "The classic midrash on Genesis, full of story and deeper meaning.",
  "shemot rabbah": "Exodus through the lens of midrash: full of story, meaning, and surprise.",
  "vayikra rabbah": "Leviticus unwrapped: midrash's stories, teachings, and deeper layers.",
  "bamidbar rabbah": "Numbers explored through midrash: packed with stories and meaning.",
  "bemidbar rabbah": "Numbers explored through midrash: packed with stories and meaning.",
  "devarim rabbah": "Deuteronomy revealed: midrash's stories, lessons, and hidden layers.",
  "midrash tanchuma": "Midrash told as sermons, full of stories and lessons for living.",
  tanchuma: "Sermons woven from Torah stories, brimming with life lessons.",
  sifrei: "Early midrash that unearths law and wisdom buried in Numbers and Deuteronomy.",
  sifra: "Early midrash that unwraps every law of Leviticus to find its deepest meaning.",
  mekhilta: "Early midrash that draws law and legend from every corner of Exodus.",
  "pirkei derabbi eliezer": "A vivid, legend-filled retelling of the Torah's great stories.",
  "yalkut shimoni": "A vast treasury gathering midrashim from across the tradition.",
  zohar: "The foundational book of Kabbalah, the Torah's hidden mystical soul.",
  "tikkunei zohar": "A central work of the Zohar, seventy meditations on the word 'Bereshit'.",
  "shaar hapesukim": "The Arizal's kabbalistic teachings on the verses, recorded by Chaim Vital.",
  "ein yaakov": "All the Talmud's stories and wisdom, gathered without the legal debate.",

  // --- Talmud (Bavli) tractates, named by their TOPIC (the name alone rarely
  //     tells a beginner what the tractate is about) ---
  berakhot: "Blessings, prayer, and the Shema, across the Talmud.",
  shabbat: "The vast laws and spirit of Shabbat, in the Talmud.",
  eruvin: "Shabbat boundaries and the eruv, in the Talmud.",
  pesachim: "Passover, chametz, and the seder night, in the Talmud.",
  shekalim: "The Temple's annual half-shekel and its upkeep, in the Talmud.",
  yoma: "Yom Kippur and the high priest's Temple service, in the Talmud.",
  sukkah: "Sukkot, the sukkah, and the four species, in the Talmud.",
  beitzah: "The laws of Yom Tov and festival cooking, in the Talmud.",
  "rosh hashanah": "The new year, the calendar, and the shofar, in the Talmud.",
  taanit: "Fast days and prayers for rain, in the Talmud.",
  megillah: "Purim, the megillah, and the Torah reading, in the Talmud.",
  "moed katan": "Chol HaMoed and the laws of mourning, in the Talmud.",
  chagigah: "The festival pilgrimage and its offerings, in the Talmud.",
  yevamot: "Levirate marriage and family law, in the Talmud.",
  ketubot: "Marriage, the ketubah, and a couple's obligations, in the Talmud.",
  nedarim: "Vows and how they are annulled, in the Talmud.",
  nazir: "The laws of the nazirite, in the Talmud.",
  sotah: "The suspected wife and her ordeal, in the Talmud.",
  gittin: "Divorce and the laws of the get, in the Talmud.",
  kiddushin: "Betrothal, marriage, and Jewish lineage, in the Talmud.",
  "bava kamma": "Damages, torts, and liability, in the Talmud.",
  "bava metzia": "Property, loans, and lost objects, in the Talmud.",
  "bava batra": "Real estate, partnership, and inheritance, in the Talmud.",
  sanhedrin: "Courts, capital law, and the foundations of faith, in the Talmud.",
  makkot: "Lashes, cities of refuge, and false witnesses, in the Talmud.",
  shevuot: "Oaths and their consequences, in the Talmud.",
  "avodah zarah": "Idolatry and its boundaries, in the Talmud.",
  horayot: "Mistaken rulings of the high court, in the Talmud.",
  zevachim: "The Temple's animal offerings, in the Talmud.",
  menachot: "Meal offerings, tzitzit, and tefillin, in the Talmud.",
  chullin: "Kosher slaughter and the dietary laws, in the Talmud.",
  bekhorot: "Firstborns, human and animal, in the Talmud.",
  arakhin: "Temple valuations and dedications, in the Talmud.",
  temurah: "Substituting consecrated animals, in the Talmud.",
  keritot: "Sins that carry the penalty of karet, in the Talmud.",
  meilah: "Misusing consecrated Temple property, in the Talmud.",
  tamid: "The daily Temple offering, in the Talmud.",
  niddah: "The laws of family purity, in the Talmud.",
  "pirkei avot": "The Mishnah's beloved collection of ethical wisdom, the Ethics of the Fathers.",

  // --- Jewish Thought: Chassidut, Mussar, Kabbalah, and philosophy ---
  tanya: "The foundational text of Chabad chassidic thought, by the Alter Rebbe.",
  "likutei moharan": "Rebbe Nachman of Breslov's profound and original teachings.",
  "likutei halakhot": "Reb Nosson of Breslov weaving Rebbe Nachman's teachings into the laws.",
  "kedushat levi": "Levi Yitzchak of Berditchev's warm, beloved chassidic classic.",
  "sefat emet": "The Gerrer Rebbe's luminous, beloved chassidic Torah teachings.",
  "noam elimelekh": "Elimelech of Lizhensk's foundational classic of the chassidic movement.",
  "mei hashiloach": "The Izhbitzer Rebbe's bold, provocative chassidic thought.",
  "toldot yaakov yosef": "The first chassidic book ever printed, from a close student of the Baal Shem Tov.",
  "ben porat yosef": "An early chassidic classic from a leading disciple of the Baal Shem Tov.",
  "ketonet pasim": "Early chassidic Torah teachings from a foundational master of the movement.",
  "degel machaneh ephraim": "Torah teachings from the Baal Shem Tov's own grandson.",
  "baal shem tov": "The collected Torah teachings of the founder of the chassidic movement.",
  "maor vashemesh": "A rich chassidic Torah commentary from the school of the Rebbe Elimelech.",
  "ohev yisrael": "The Apter Rav's teachings, radiating his famous love for every Jew.",
  "tiferet shlomo": "The Radomsker Rebbe's beloved chassidic discourses.",
  "peri tzadik": "Tzadok HaKohen of Lublin's deep, sweeping chassidic thought.",
  "shem mishmuel": "The Sochatchover Rebbe's expansive discourses on the weekly parsha.",
  "agra dekala": "The Bnei Yissaschar's chassidic and kabbalistic commentary on the Torah.",
  "arvei nachal": "Stirring chassidic and mussar sermons on the weekly parsha.",
  "yismach moshe": "Chassidic Torah teachings from the Yismach Moshe, who helped bring the movement to Hungary.",
  "shenei luchot haberit": "The Shelah's monumental blend of law, kabbalah, and ethics.",
  "torat haolah": "The Rema's philosophical work on the Temple and its meaning.",
  "akeidat yitzchak": "Philosophical sermons probing the deeper themes of each parsha.",
  "megalleh amukkot": "Nathan Nata Spira's kabbalistic flights of gematria and hidden hints.",
  "ben ish hai": "The Ben Ish Hai's beloved blend of law and kabbalah, arranged by parsha.",
  "mesilat yesharim": "The Ramchal's step-by-step classic on character and spiritual growth.",
  "chovot halevavot": "Bachya ibn Pakuda's timeless guide to the duties of the heart.",
  kuzari: "Yehuda HaLevi's spirited defense of Judaism, told as a dialogue.",
  "guide for the perplexed": "Maimonides' landmark work reconciling Torah and philosophy.",
  "moreh nevukhim": "Maimonides' landmark work reconciling Torah and philosophy.",
  "derekh hashem": "The Ramchal's clear, systematic map of Jewish belief.",
  "nefesh hachaim": "Rabbi Chaim of Volozhin's profound work on the soul and Torah study.",
  "orchot tzadikim": "A classic, accessible guide to character and virtue.",
  "reishit chochmah": "A sweeping classic of kabbalistic ethics and self-refinement.",
  "pele yoetz": "Warm, practical guidance for everyday Jewish life, A to Z.",

  // --- more Midrash / aggadah ---
  "midrash aggadah": "A medieval anthology of story-based, aggadic midrash.",
  "midrash lekach tov": "An 11th-century midrashic digest on the Torah, also called Pesikta Zutarta.",
  "sefer hayashar": "A medieval narrative retelling of the Torah's stories.",
  "bereshit rabbati": "A later midrashic expansion on Genesis, from the school of Moshe HaDarshan.",
  "midrash tannaim": "An early halachic midrash on the book of Deuteronomy.",
  "otzar midrashim": "A vast anthology gathering hundreds of small midrashim.",
  "midrash tehillim": "The classic midrash on the book of Psalms.",
  "pesikta derav kahana": "An early homiletical midrash on the festivals and special readings.",
  "pesikta rabbati": "A homiletical midrash on the holidays and special Shabbatot.",

  // --- supercommentaries on Rashi + commentaries on the Targum ---
  "divrei david": "The Taz's supercommentary clarifying Rashi on the Torah.",
  "levush haorah": "The Levush's supercommentary defending and explaining Rashi.",
  "maskil ledavid": "Rabbi David Pardo's clear, careful supercommentary on Rashi.",
  "tzror hamor": "Avraham Saba's kabbalistically rich commentary on the Torah.",
  bartenura: "The Bartenura's supercommentary on Rashi, by the famed Mishnah commentator.",
  "tafsir rasag": "Saadia Gaon's classic Arabic translation of the Torah.",
  "minei targuma": "A commentary illuminating the Targum's Aramaic translation.",
  "netinah lager": "Rabbi Nathan Adler's commentary on Targum Onkelos.",
  "ohev ger": "Shadal's scholarly study of Targum Onkelos.",

  // --- coverage additions (2026-06-18): prominent in-taxonomy works that were
  //     appearing with no description. Only works whose author + character are
  //     hand-verified are added; uncertain ones stay omitted (ACCURACY-LOCK). ---
  // Halacha (codes)
  "mishneh torah": "Rambam's sweeping code that organizes all of Jewish law in clear Hebrew.",
  "sefer hamitzvot": "Rambam's reckoning and definition of the 613 commandments.",
  "shulchan arukh": "Rabbi Yosef Karo's authoritative code of practical Jewish law.",
  "sefer hachinukh": "Walks the 613 mitzvot in parsha order, each command with its reason.",
  "sefer mitzvot gadol": "The Semag, a major medieval count of the 613 commandments.",
  "arukh hashulchan": "Rav Yechiel Michel Epstein's flowing restatement of the codified law.",
  tur: "Rav Yaakov ben Asher's great law code, the frame for the Shulchan Aruch.",
  "tur haarokh": "The fuller Torah commentary of the master who codified Jewish law.",
  // Commentary
  "em lamikra": "Rav Eliyahu Benamozegh's wide-ranging 19th-century Torah commentary.",
  "david zvi hoffmann": "The great German rabbi's rigorous scholarship, defending tradition with care.",
  recanati: "Menachem Recanati's classic kabbalistic reading of the Torah.",
  "nachal kedumim": "The Chida's gem-filled anthology of insights on the parsha.",
  "penei david": "The Chida's flowing derashot and insights on the Torah.",
  steinsaltz: "Rav Adin Steinsaltz's clear, modern elucidation that opens up the text.",
  // Midrash
  "sifrei zuta": "An early tannaitic midrash drawing law from the book of Numbers.",
  "tanna debei eliyahu": "An early midrash of ethics and faith, taught as Eliyahu's lessons.",
  // Jewish Thought: Maharal, philosophy, mussar
  "gevurot hashem": "The Maharal's deep, systematic work on the Exodus and redemption.",
  "netzach yisrael": "The Maharal's profound study of exile, redemption, and Israel's destiny.",
  "netivot olam": "The Maharal's classic on character, ethics, and the paths of life.",
  "beer hagolah": "The Maharal's spirited defense of the words of the Sages.",
  "haemunot vehadeot": "Saadia Gaon's pioneering work of Jewish belief and philosophy.",
  "kad hakemach": "Rabbeinu Bachya's alphabetical guide to faith and good character.",
  "menorat hamaor": "A beloved medieval anthology of the Talmud's ethics and aggadah.",
  "shemirat halashon": "The Chofetz Chaim's classic on the power and guarding of speech.",
  "covenant and conversation": "Rabbi Jonathan Sacks' eloquent essays on the weekly parsha.",
  // Chassidut
  "divrei emet": "Chassidic Torah from the Chozeh of Lublin, the great seer and early Rebbe.",
  "beer mayim chaim": "Rav Chaim of Chernovitz's warm, flowing chassidic Torah commentary.",
  "bat ayin": "The warm chassidic Torah of Rav Avraham Dov of Avritch.",
  "zera kodesh": "Chassidic Torah from Rav Naftali Tzvi of Ropshitz.",
  "ohr hameir": "Chassidic discourses from Rav Zev Wolf of Zhitomir, the Maggid's disciple.",
  "likkutei torah": "The Alter Rebbe of Chabad's chassidic discourses on the Torah.",
  "torah ohr": "The Alter Rebbe of Chabad's foundational chassidic discourses.",
  // Key-spelling fixes: these descriptions existed but the key did not match
  // Sefaria's actual index_title spelling, so they never resolved.
  "nefesh hachayim": "Rabbi Chaim of Volozhin's profound work on the soul and Torah study.",
  "mesillat yesharim": "The Ramchal's step-by-step classic on character and spiritual growth.",
};

// Resolve a description for a source by its commentator/work name, then the
// "X on Y" head ("Rashi on Numbers" -> "rashi"), then a prefix form so per-book
// titles match their work ("Onkelos Numbers" -> "onkelos", "Sifrei Bamidbar" ->
// "sifrei"). Returns null when we have no confident description (the card then
// simply shows no description line - never a guess).
export function describeSource(commentator: string, ref = ""): string | null {
  const name = norm(commentator);
  if (DESCRIPTIONS[name]) return DESCRIPTIONS[name];
  if (/ on /i.test(ref)) {
    const head = norm(ref.split(/ on /i)[0]);
    if (DESCRIPTIONS[head]) return DESCRIPTIONS[head];
  }
  // Prefix match: a per-book / segmented title that starts with a known work key
  // ("onkelos numbers", "sifrei bamidbar 1"). Longest key first so the most
  // specific entry wins ("midrash tanchuma" before "tanchuma").
  for (const key of PREFIX_KEYS) {
    if (name.startsWith(key + " ")) return DESCRIPTIONS[key];
  }
  // Any "<Book> Rabbah" (Eikhah Rabbah, Ruth Rabbah, Kohelet Rabbah, ...) is a
  // midrash rabbah - safe generic when no more specific entry matched above.
  if (/(^|\s)rabbah(\s|$)/.test(name)) return DESCRIPTIONS["midrash rabbah"];
  return null;
}

const PREFIX_KEYS = Object.keys(DESCRIPTIONS).sort((a, b) => b.length - a.length);

// Exposed for tests/tools that want to audit coverage.
export const DESCRIBED_KEYS: readonly string[] = Object.keys(DESCRIPTIONS);
